import { useState } from 'react';
import { useParams } from 'react-router-dom';

import IterationPageHeader from 'components/IterationPageHeader';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useCampaign from 'hooks/useCampaign';
import { IIterationLevelParams } from 'utils/parameterUtils';
import { Button, Card } from 'nhsuk-react-components';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import RoutingPlansUpdated from 'components/RoutingPlansUpdated';
import useFetchTeamRoutingPlans from 'queries/fetch/fetchTeamRoutingPlans';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import featureFlags from 'services/featureFlags';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { ApiError } from 'schemas/ApiError.schema';
import { API_UPDATE_ROUTING_PLANS_ERROR } from 'constants/errorMessages';
import {
  getLatestRoutingPlansArray,
  getLatestRoutingPlansString,
  getOldRoutingPlans, getOldRoutingRule, getOldRoutingRules,
} from 'utils/routingPlansUtils';
import useUpdateCampaign from 'queries/mutate/updateCampaign';
import useCampaignLock from 'hooks/useCampaignLock';
import useIterationLock from 'hooks/useIterationLock';

function RoutingPlansUpdatedPage() {
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const { campaign } = useCampaign();
  const { navigateToCampaign } = useCampaignNavigate();
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: teamRoutingPlans } = useFetchTeamRoutingPlans({ teamId, enabled: featureFlags.notifyRoutingPlansEnabled });
  const updateIteration = useUpdateIteration();
  const updateCampaign = useUpdateCampaign();
  const { lockCampaignIfRequired } = useCampaignLock({ teamId, campaignId });
  const { lockIterationIfRequired } = useIterationLock({ teamId, campaignId, iterationId });

  const currentIteration = campaign?.CampaignConfig?.Iterations?.find(
    (iteration) => iteration.ID === iterationId,
  );

  const oldRoutingPlans = getOldRoutingPlans(teamRoutingPlans, currentIteration?.DefaultCommsRouting);
  const oldRoutingRules = getOldRoutingRules(teamRoutingPlans, currentIteration?.IterationRules);

  const newRoutingRules = currentIteration?.IterationRules?.filter(
    (rule) => rule.Type === 'R' && Boolean(rule.CommsRouting) && rule.CommsRouting!.includes(':'),
  );

  const handleContinue = async () => {
    const newDefaultCommsRouting = getLatestRoutingPlansString(teamRoutingPlans, currentIteration?.DefaultCommsRouting);

    const updatedRules = currentIteration?.IterationRules?.map((rule) => {
      if (rule.CommsRouting) {
        return { ...rule, CommsRouting: getLatestRoutingPlansString(teamRoutingPlans, rule.CommsRouting) };
      }
      return rule;
    });

    const updatedIteration = {
      ...currentIteration,
      DefaultCommsRouting: newDefaultCommsRouting,
      IterationRules: updatedRules,
    };

    try {
      setIsSubmitting(true);

      await lockIterationIfRequired();

      await updateIteration.mutateAsync({
        campaignId,
        iterationId,
        teamId,
        iteration: updatedIteration as IterationDetailsFormValues,
      });

      await lockCampaignIfRequired();

      const latestAvailableRoutingIds = getLatestRoutingPlansArray(teamRoutingPlans, campaign.CampaignConfig.AvailableRoutingIds);

      await updateCampaign.mutateAsync({
        teamId,
        campaignId,
        campaign: {
          ...campaign,
          CampaignConfig: {
            ...campaign.CampaignConfig,
            Iterations: campaign.CampaignConfig.Iterations?.map((iteration) => (
              iteration.ID === iterationId ? updatedIteration : iteration
            )),
            AvailableRoutingIds: latestAvailableRoutingIds,
          },
        },
      });

      navigateToCampaign(`iteration/${iterationId}/reviewers`);
    } catch {
      setError({ message: API_UPDATE_ROUTING_PLANS_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="iteration-reviewers-page">
      {currentIteration && (
        <IterationPageHeader
          iteration={currentIteration}
          title="Message plans updated"
        />
      )}

      <Card>
        <>
          {oldRoutingPlans.length > 0 || oldRoutingRules.length > 0 ? (
            <>
              <p>
                These message plans and/or rules have been updated for your iteration.
              </p>
              <p>
                Contact your administrator if you&apos;re unsure about these changes.
              </p>
            </>
          ) : (
            <p>Default message plans are up to date.</p>
          )}

          {oldRoutingPlans.length > 0 && (
            <>
              <h3 className="nhsuk-u-font-weight-bold">
                Default message
                {' '}
                {oldRoutingPlans.length === 2 ? 'plan' : 'plans'}
                {' '}
                updated
              </h3>
              <RoutingPlansUpdated plans={oldRoutingPlans} />
            </>
          )}

          {oldRoutingRules.length > 0 && (
            <h3 className="nhsuk-u-font-weight-bold">
              Routing
              {' '}
              {oldRoutingRules.length === 2 ? 'rule' : 'rules'}
              {' '}
              updated
            </h3>
          )}

          {newRoutingRules && newRoutingRules.map((item) => {
            const oldRoutingRule = getOldRoutingRule(teamRoutingPlans, item.CommsRouting!);

            if (!oldRoutingRule.length) {
              return null;
            }

            return (
              <div key={item.ID} className="nhsuk-u-margin-bottom-4">
                <h4>
                  Rule name:
                  {' '}
                  {item.Name}
                </h4>
                <RoutingPlansUpdated plans={oldRoutingRule} />
              </div>
            );
          })}

        </>
      </Card>
      <ApiErrorDetails apiError={error} />
      <Button onClick={handleContinue} disabled={isSubmitting} className="nhsuk-u-margin-top-4">
        {isSubmitting ? 'Updating...' : 'Continue'}
      </Button>
    </div>
  );
}

export default RoutingPlansUpdatedPage;
